import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  Globe,
  Loader2,
  Save,
  Trash2,
} from "lucide-react";
import type {
  AccountDTO,
  DraftDTO,
  PublishWpInput,
  UpdateArticleInput,
} from "@app/shared";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { useToast } from "../components/Toast";

interface SeoMeta {
  title?: string;
  description?: string;
  slug?: string;
}

interface FormState {
  headline: string;
  bodyHtml: string;
  seoTitle: string;
  seoDescription: string;
  slug: string;
  wpCategoryId: string;
}

/** Best-effort read of the stored seoMeta JSON blob. */
function readSeo(value: unknown): SeoMeta {
  if (typeof value !== "object" || value === null) return {};
  const v = value as Record<string, unknown>;
  return {
    title: typeof v.title === "string" ? v.title : undefined,
    description: typeof v.description === "string" ? v.description : undefined,
    slug: typeof v.slug === "string" ? v.slug : undefined,
  };
}

function draftToForm(d: DraftDTO): FormState {
  const seo = readSeo(d.seoMeta);
  return {
    headline: d.headline ?? "",
    bodyHtml: d.bodyHtml ?? "",
    seoTitle: seo.title ?? "",
    seoDescription: seo.description ?? "",
    slug: seo.slug ?? "",
    wpCategoryId: d.wpCategoryId != null ? String(d.wpCategoryId) : "",
  };
}

/** Construct the public WordPress permalink from the site URL + post id. */
function wpPostLink(siteUrl: string | null, wpPostId: number | null): string | null {
  if (!siteUrl || wpPostId == null) return null;
  return `${siteUrl.replace(/\/$/, "")}/?p=${wpPostId}`;
}

/**
 * Article editor: edit title/body/SEO/category, publish to WordPress, and view
 * the resulting post id/link once posted.
 */
export function ArticleDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const articleQuery = useQuery({
    queryKey: ["articles", "detail", id],
    queryFn: ({ signal }) => api.article(id, signal),
    enabled: id.length > 0,
  });

  const accountsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: ({ signal }) => api.accounts(signal),
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (articleQuery.data) setForm(draftToForm(articleQuery.data));
  }, [articleQuery.data]);

  const article = articleQuery.data;
  const editable =
    article != null &&
    (article.status === "draft" || article.status === "approved");

  // The account whose WP site this article publishes to, used only to build the
  // permalink for display. The draft carries `accountId` (a cuid), not the key,
  // so we resolve to the first WordPress-connected account as a best effort.
  const account: AccountDTO | undefined = useMemo(() => {
    if (!accountsQuery.data) return undefined;
    return accountsQuery.data.find((a) => a.wpConnected) ?? accountsQuery.data[0];
  }, [accountsQuery.data]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const save = useMutation({
    mutationFn: () => {
      if (!form) throw new Error("Form not ready");
      const seoMeta: SeoMeta = {
        title: form.seoTitle || undefined,
        description: form.seoDescription || undefined,
        slug: form.slug || undefined,
      };
      const payload: UpdateArticleInput = {
        headline: form.headline,
        bodyHtml: form.bodyHtml,
        seoMeta,
      };
      const trimmedCat = form.wpCategoryId.trim();
      if (trimmedCat !== "") {
        const parsed = Number(trimmedCat);
        if (Number.isInteger(parsed)) payload.wpCategoryId = parsed;
      }
      return api.updateArticle(id, payload);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["articles", "detail", id], updated);
      void queryClient.invalidateQueries({ queryKey: ["articles"] });
      toast.success("Article saved");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  const publish = useMutation({
    mutationFn: () => {
      const payload: PublishWpInput = { status: "publish" };
      return api.publishArticle(id, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["articles"] });
      void queryClient.invalidateQueries({ queryKey: ["articles", "detail", id] });
      toast.success("Publish ke WordPress diminta — status akan diperbarui");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Publish failed"),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteArticle(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["articles"] });
      toast.success("Article deleted");
      navigate("/articles", { replace: true });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  if (articleQuery.isLoading || !form) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-teal" />
      </div>
    );
  }

  if (articleQuery.isError || !article) {
    return (
      <Card>
        <CardContent className="space-y-3 p-8 text-center">
          <p className="text-sm text-coral">Article not found.</p>
          <Button variant="outline" onClick={() => navigate("/articles")}>
            Back to articles
          </Button>
        </CardContent>
      </Card>
    );
  }

  const canPublish =
    article.status === "draft" || article.status === "approved";
  const postLink = wpPostLink(account?.wpSiteUrl ?? null, article.wpPostId);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/articles"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-navy">
              Article detail
            </h1>
            <p className="text-xs text-slate-500">{article.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={article.status} />
          <Badge variant="outline">{article.mode}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Edit column */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!editable ? (
                <p className="rounded-md bg-slate-100 p-2 text-xs text-slate-500">
                  This article is {article.status} and is read-only.
                </p>
              ) : null}
              <div className="space-y-1.5">
                <Label htmlFor="headline">Title</Label>
                <Input
                  id="headline"
                  value={form.headline}
                  disabled={!editable}
                  onChange={(e) => setField("headline", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bodyHtml">Body (HTML)</Label>
                <Textarea
                  id="bodyHtml"
                  rows={20}
                  className="font-mono text-xs"
                  value={form.bodyHtml}
                  disabled={!editable}
                  onChange={(e) => setField("bodyHtml", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">SEO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="seoTitle">SEO title</Label>
                <Input
                  id="seoTitle"
                  value={form.seoTitle}
                  disabled={!editable}
                  onChange={(e) => setField("seoTitle", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seoDescription">Meta description</Label>
                <Textarea
                  id="seoDescription"
                  rows={3}
                  value={form.seoDescription}
                  disabled={!editable}
                  onChange={(e) => setField("seoDescription", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="slug">Slug</Label>
                  <Input
                    id="slug"
                    value={form.slug}
                    disabled={!editable}
                    onChange={(e) => setField("slug", e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="wpCategoryId">WP category id</Label>
                  <Input
                    id="wpCategoryId"
                    type="number"
                    inputMode="numeric"
                    value={form.wpCategoryId}
                    disabled={!editable}
                    onChange={(e) => setField("wpCategoryId", e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Side column: status + actions */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">WordPress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">Status</span>
                <StatusBadge status={article.status} />
              </div>
              {article.wpPostId != null ? (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">WP post id</span>
                  <span className="font-mono text-xs">{article.wpPostId}</span>
                </div>
              ) : null}
              {postLink ? (
                <a
                  href={postLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-teal hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  View on WordPress
                </a>
              ) : null}
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">Updated</span>
                <span className="text-right text-xs font-medium">
                  {formatDateTime(article.updatedAt)}
                </span>
              </div>
              {article.errorMsg ? (
                <p className="rounded-md bg-coral/10 p-2 text-xs text-coral">
                  {article.errorMsg}
                </p>
              ) : null}
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2">
            <Button
              variant="default"
              onClick={() => save.mutate()}
              disabled={!editable || save.isPending}
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save
            </Button>
            {canPublish ? (
              <Button
                variant="primary"
                onClick={() => publish.mutate()}
                disabled={publish.isPending}
              >
                {publish.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Globe className="h-4 w-4" />
                )}
                Publish ke WordPress
              </Button>
            ) : null}
            <Button
              variant="destructive"
              onClick={() => setConfirmDelete(true)}
              disabled={remove.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent onClose={() => setConfirmDelete(false)}>
          <DialogHeader>
            <DialogTitle>Delete this article?</DialogTitle>
            <DialogDescription>
              This permanently removes the article draft. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
